import { TextInput, TextInputProps, get } from '@vtex/admin-ui'
import { FormState, useWatch } from '@vtex/admin-ui-form'
import { useAdmin } from '@vtex/raccoon-next'
import getSymbolFromCurrency from 'currency-symbol-map'
import { useCallback, useEffect, useState } from 'react'
import { CURRENCY_FRACTION_DIGITS } from 'shared'
import { useNumbers } from '../utils/useNumbers'

interface Props {
  name: string
  label?: React.ReactNode
  state: FormState
  readOnly?: boolean
}

const DEFAULT_CURRENCY = 'USD'

export function CurrencyInput(props: Props & TextInputProps) {
  const { name, state, readOnly = false, onChange, ...rest } = props

  const { toLocaleNumber, formatNumber } = useNumbers()

  const { currency = DEFAULT_CURRENCY } = useAdmin()

  const value: number | undefined = useWatch({ form: state, name })

  const currencySymbol = getSymbolFromCurrency(currency)

  const formatCurrency = useCallback(
    (number: number | undefined) => {
      if (number === undefined) return ''

      return formatNumber(number, {
        minimumFractionDigits: CURRENCY_FRACTION_DIGITS,
        maximumFractionDigits: CURRENCY_FRACTION_DIGITS,
      })
    },
    [formatNumber]
  )

  useEffect(() => {
    setInputValue(formatCurrency(value))
  }, [value, formatCurrency])

  const [inputValue, setInputValue] = useState(formatCurrency(value))

  const errorMessage = get(state.formState.errors, name)?.message

  function handleBlur(event: React.FocusEvent<HTMLInputElement>) {
    const inputValue = event.target.value

    let valueToSave

    if (inputValue === '') {
      valueToSave = undefined
    } else {
      const valueAsNumber = toLocaleNumber(event.target.value)
      valueToSave = Number(valueAsNumber.toFixed(CURRENCY_FRACTION_DIGITS))
    }

    state.setValue(name, valueToSave)
    setInputValue(formatCurrency(valueToSave))
  }

  return (
    <TextInput
      {...rest}
      value={inputValue}
      step={0.01}
      prefix={currencySymbol}
      disabled={readOnly}
      onChange={(event) => {
        setInputValue(event.target.value)
        onChange?.(event)
      }}
      onBlur={handleBlur}
      error={Boolean(errorMessage)}
      errorText={errorMessage}
    />
  )
}
